import Vue from 'vue';
import axios from 'axios';
import { message, notification } from 'ant-design-vue';
import { i18n } from '@/services/i18n';
import { parseJwt } from '@/utils/jwt';
import { isLoginPage } from '@/utils/isLoginPage';
import { isAuthCallbackPage } from '@/utils/isAuthCallbackPage';
import SessionGate from '@/components/UI/SessionGate.vue';
import {
    bearerOf, classify, FailureEnv, FailureKind, FATAL, isMixedContentSuspect, isOurBackend,
    isOurHost, joinUrl, LocalTokenKind, SEVERITY, Verdict,
} from './classify';
import { getEnv } from '@/runtime-config';

function isKeycloakMode(): boolean {
    return getEnv('AUTH_MODE') === 'keycloak';
}

const RETURN_KEY = 'sp_return_url';
const REASON_KEY = 'sp_return_reason';

/* 12 часов: карточка висит бесконечно, адрес возврата должен пережить долгий перерыв */
const RETURN_TTL_MS = 12 * 60 * 60 * 1000;
const RETURN_MAX_LEN = 512;

/* Не localStorage: LocalData чистит его при смене версии раньше, чем значение прочитают */
function safeSessionSet(key: string, value: string): void {
    try {
        window.sessionStorage.setItem(key, value);
    } catch (e) { /* приватный режим Safari — живём без returnUrl */ }
}

function safeSessionGet(key: string): string | null {
    try {
        return window.sessionStorage.getItem(key);
    } catch (e) {
        return null;
    }
}

function safeSessionRemove(key: string): void {
    try {
        window.sessionStorage.removeItem(key);
    } catch (e) { /* ignore */ }
}

/* Только относительный путь своего приложения: '//host' — open redirect */
function isSafeReturnPath(path: string | null | undefined): boolean {
    if (!path || typeof path !== 'string') { return false; }
    if (path.length > RETURN_MAX_LEN) { return false; }
    // Управляющие символы браузер вырезает из адреса: '/<tab>/host' станет '//host'
    if (/[\u0000-\u001f\u007f]/.test(path)) { return false; }
    if (path.charAt(0) !== '/') { return false; }
    if (path.charAt(1) === '/' || path.charAt(1) === '\\') { return false; }
    if (/^\/(login|authorization)(\/|\?|#|$)/i.test(path)) { return false; }
    return true;
}

export function rememberReturnUrl(explicitPath?: string): void {
    const path = explicitPath
        ? explicitPath
        : (window.location.pathname + window.location.search + window.location.hash);
    if (!isSafeReturnPath(path)) { return; }
    safeSessionSet(RETURN_KEY, JSON.stringify({ path: path, at: Date.now() }));
}

export function consumeReturnUrl(): string | null {
    const raw = safeSessionGet(RETURN_KEY);
    safeSessionRemove(RETURN_KEY);
    if (!raw) { return null; }
    let parsed: any = null;
    try {
        parsed = JSON.parse(raw);
    } catch (e) {
        return null;
    }
    if (!parsed || !parsed.path || !parsed.at) { return null; }
    if (Date.now() - Number(parsed.at) > RETURN_TTL_MS) { return null; }
    if (!isSafeReturnPath(parsed.path)) { return null; }
    return String(parsed.path);
}

/* Формат 'KIND|SUB' разбирает Auth.vue — менять нельзя */
export function saveReason(kind: string, sub: string): void {
    const reason = String(kind) + (sub ? '|' + String(sub) : '');
    safeSessionSet(REASON_KEY, JSON.stringify({ reason: reason, at: Date.now() }));
}

export function consumeReason(): string {
    const raw = safeSessionGet(REASON_KEY);
    safeSessionRemove(REASON_KEY);
    if (!raw) { return ''; }
    let parsed: any = null;
    try {
        parsed = JSON.parse(raw);
    } catch (e) {
        return '';
    }
    if (!parsed || typeof parsed.reason !== 'string' || !parsed.at) { return ''; }
    if (Date.now() - Number(parsed.at) > RETURN_TTL_MS) { return ''; }
    return parsed.reason;
}

type QuietLevel = 'error' | 'info';

let quietInstalled = false;

function levelOf(args: any): QuietLevel {
    const type = args && typeof args === 'object' ? String(args.type || '') : '';
    return (type === 'error' || type === 'warning') ? 'error' : 'info';
}

/* antd message.open отдаёт функцию-закрывалку с then/promise — заглушка повторяет её форму */
function mutedMessageResult(): any {
    const noop: any = function (): void { /* нечего закрывать */ };
    noop.promise = Promise.resolve();
    noop.then = function (filled: any, rejected: any): any {
        return noop.promise.then(filled, rejected);
    };
    return noop;
}

/* antd 1.7.8 зовёт api.open в момент вызова, поэтому подмена .open перехватывает весь канал */
function installQuietWrapper(): void {
    if (quietInstalled) { return; }
    quietInstalled = true;

    const n: any = notification;
    if (typeof n.open === 'function') {
        const originalNotificationOpen = n.open;
        n.open = function (args: any): any {
            if (isSilenced(levelOf(args))) {
                return undefined;
            }
            return originalNotificationOpen.call(n, args);
        };
    }

    const m: any = message;
    if (typeof m.open === 'function') {
        const originalMessageOpen = m.open;
        m.open = function (args: any): any {
            if (isSilenced(levelOf(args))) {
                return mutedMessageResult();
            }
            return originalMessageOpen.call(m, args);
        };
    }
}

/* В antd 1.7.8 destroy() удаляет контейнер из кеша, следующий open() создаст его заново */
function destroyAllToasts(): void {
    try { (notification as any).destroy(); } catch (e) { /* контейнера может не быть */ }
    try { (message as any).destroy(); } catch (e) { /* контейнера может не быть */ }
}

interface GateState {
    kind: FailureKind | null;
    sub: string;
    pulseSeq: number;
    behind: boolean;
    busy: string;
}

/* Vue 2.6: реактивны только поля, заданные в момент вызова observable — добавлять позже нельзя */
export const gate: GateState = Vue.observable<GateState>({
    kind: null,
    sub: '',
    pulseSeq: 0,
    behind: false,
    busy: '',
});

let openPanels = 0;

export function notePanelOpened(): void {
    if (isKeycloakMode()) { return; }
    openPanels = openPanels + 1;
    if (cardIsUp()) { gate.behind = true; }
}

export function notePanelClosed(): void {
    if (isKeycloakMode()) { return; }
    openPanels = openPanels > 0 ? openPanels - 1 : 0;
    if (openPanels === 0) { gate.behind = false; }
}

const CLOCK_SKEW_MS = 120000;

const PULSE_THROTTLE_MS = 700;

const USER_INTENT_WINDOW_MS = 3000;

export const LOGOUT_MARKER = 'sp_logout_marker';

function logoutMarked(): boolean {
    try {
        return localStorage.getItem(LOGOUT_MARKER) === '1';
    } catch (e) {
        return false;
    }
}

let unloading = false;
let lastPulseAt = 0;
let lastIntentAt = 0;
let requestFailedSinceIntent = false;

let backendAnswered = false;

/* location.replace меняет документ не сразу: до коммита запросы вернут 401 уже отменённой сессии */
let leavingToLogin = false;

export function noteLeavingToLogin(): void {
    leavingToLogin = true;
}

export function isLeavingToLogin(): boolean {
    return leavingToLogin;
}

function apiBaseUrl(): string {
    return getEnv('BACKEND_URL') ? String(getEnv('BACKEND_URL')) : '';
}

function markUnloading(): void {
    unloading = true;
}

function clearUnloading(): void {
    unloading = false;
}

function onPageHide(ev: any): void {
    if (ev && ev.persisted) {
        clearUnloading(); // уход в bfcache: страница ещё вернётся живой
        return;
    }
    markUnloading();
}

function onPageShow(ev: any): void {
    clearUnloading();
    // Возврат из bfcache: пока страница была заморожена, события storage до неё не доходили
    if (ev && ev.persisted) { onWake(); }
}

const JWT_SHAPE_RE = /^[A-Za-z0-9_+\/=-]+\.[A-Za-z0-9_+\/=-]+\.[A-Za-z0-9_+\/=-]*$/;

let lastJwtToken = '';
let lastJwtExp = 0;

function tokenExpSeconds(token: string): number {
    if (token === lastJwtToken) { return lastJwtExp; }
    lastJwtToken = token;
    lastJwtExp = 0;
    if (!JWT_SHAPE_RE.test(token)) { return 0; }
    const payload = parseJwt(token);
    const exp = (payload && payload.exp) ? Number(payload.exp) : 0;
    lastJwtExp = exp > 0 ? exp : 0;
    return lastJwtExp;
}

export function isLocalStorageUsable(): boolean {
    try {
        localStorage.getItem('AUTH');
        return true;
    } catch (e) {
        return false;
    }
}

function readAuthToken(): string {
    let raw: string | null = null;
    try {
        raw = localStorage.getItem('AUTH');
    } catch (e) {
        return '';
    }
    if (!raw) { return ''; }
    try {
        const parsed = JSON.parse(raw);
        return (parsed && parsed.access_token) ? String(parsed.access_token) : '';
    } catch (e) {
        return '';
    }
}

export function readLocalTokenKind(): LocalTokenKind {
    let raw: string | null = null;
    try {
        raw = localStorage.getItem('AUTH');
    } catch (e) {
        return 'ABSENT';
    }
    if (!raw) {
        return 'ABSENT';
    }
    let parsed: any = null;
    try {
        parsed = JSON.parse(raw);
    } catch (e) {
        return 'CORRUPT';
    }
    const token = parsed && parsed.access_token ? String(parsed.access_token) : '';
    if (!token) {
        return 'CORRUPT';
    }
    const exp = tokenExpSeconds(token);
    if (!exp) {
        return 'MALFORMED';
    }
    if (exp * 1000 < Date.now() - CLOCK_SKEW_MS) {
        return 'EXPIRED';
    }
    return 'FRESH';
}

function snapshotEnv(cancelled: boolean): FailureEnv {
    return {
        pageProtocol: window.location.protocol,
        pageOrigin: window.location.origin,
        apiBaseUrl: apiBaseUrl(),
        onLine: typeof navigator.onLine === 'boolean' ? navigator.onLine : true,
        unloading: unloading,
        cancelled: cancelled,
        tokenKind: readLocalTokenKind(),
        authToken: readAuthToken(),
        backendAnswered: backendAnswered,
    };
}

function cardIsUp(): boolean {
    return gate.kind !== null;
}

function isFatal(): boolean {
    return !!(gate.kind && FATAL[gate.kind]);
}

export function isSilenced(level: QuietLevel): boolean {
    if (isFatal()) {
        return true;
    }
    if (level !== 'error' || !cardIsUp()) {
        return false;
    }
    return requestFailedSinceIntent;
}

function noteIntent(): void {
    lastIntentAt = Date.now();
    requestFailedSinceIntent = false;
}

function needsRelogin(): boolean {
    return gate.kind === 'SESSION_EXPIRED' || gate.kind === 'AUTH_STORAGE_CORRUPT';
}

export function pulse(source: 'intent' | 'navigation' | 'request'): void {
    if (!cardIsUp()) { return; }
    const now = Date.now();
    if (source === 'request' && now - lastIntentAt > USER_INTENT_WINDOW_MS) {
        return;
    }

    if (now - lastPulseAt < PULSE_THROTTLE_MS) { return; }
    lastPulseAt = now;
    gate.pulseSeq = gate.pulseSeq + 1;
}

let verdictToken = '';

function foreignFreshToken(): boolean {
    return readLocalTokenKind() === 'FRESH' && readAuthToken() !== verdictToken;
}

/* Вход пишет AUTH и userData двумя действиями: перезагрузка между ними даст меню без прав */
const USER_DATA_WAIT_MS = 5000;

let resumeTimer = 0;

function cancelResume(): void {
    if (!resumeTimer) { return; }
    window.clearTimeout(resumeTimer);
    resumeTimer = 0;
}

function resumeWithNewSession(): void {
    cancelResume();
    safeSessionRemove(RETURN_KEY);
    safeSessionRemove(REASON_KEY);
    window.location.reload();
}

function hasUserData(): boolean {
    try {
        return !!localStorage.getItem('userData');
    } catch (e) {
        return true;
    }
}

/* За время ожидания вход могли отменить выходом: перезагрузка увела бы вкладку на /login молча */
function resumeAfterWait(): void {
    resumeTimer = 0;
    if (!foreignFreshToken()) { return; }
    resumeWithNewSession();
}

function resumeWhenReady(): void {
    if (hasUserData()) {
        resumeWithNewSession();
        return;
    }
    if (resumeTimer) { return; }
    resumeTimer = window.setTimeout(resumeAfterWait, USER_DATA_WAIT_MS);
}

function dropOwnSession(): boolean {
    if (foreignFreshToken()) {
        resumeWhenReady();
        return false;
    }
    try {
        // sp_notifications не трогаем: это единственный след произошедшего для пользователя
        localStorage.removeItem('AUTH');
        localStorage.removeItem('userData');
    } catch (e) { /* ignore */ }
    return true;
}

export function raise(v: Verdict, token?: string): void {
    if (leavingToLogin) { return; }
    if (isKeycloakMode()) { return; }

    const severity = SEVERITY[v.kind];
    if (severity === undefined || severity === 0) {
        return;
    }
    if (isLoginPage() || isAuthCallbackPage()) { return; }

    if (unloading && logoutMarked()) { return; }

    const current = gate.kind ? SEVERITY[gate.kind] : -1;
    if (severity < current || v.kind === gate.kind) { return; }

    if (v.kind === 'SESSION_EXPIRED' || v.kind === 'AUTH_STORAGE_CORRUPT') {
        verdictToken = token || readAuthToken();
        if (!dropOwnSession()) { return; }
    }

    gate.kind = v.kind;
    gate.sub = v.sub;
    gate.behind = openPanels > 0;
    gate.busy = '';

    if (FATAL[v.kind]) {
        rememberReturnUrl();
        saveReason(v.kind, v.sub);
        destroyAllToasts();
    }

    mountGate();
}

function clearTransient(): void {
    if (gate.kind && !FATAL[gate.kind]) {
        gate.kind = null;
        gate.sub = '';
        gate.behind = openPanels > 0;
        gate.busy = '';
    }
}

const PROBE_THROTTLE_MS = 10000;

const PROBE_TIMEOUT_MS = 8000;

let lastProbeAt = 0;
let probeInFlight: Promise<ProbeResult> | null = null;

export function blocksRequests(url?: string): boolean {
    if (!isFatal()) { return false; }
    const u = url ? String(url) : '';
    if (/(^|\/)auth\/(login|exchange-tokens|refresh)(\/|\?|#|$)/i.test(u)) { return false; }
    if (/(^|\/)(auth\/)?logout(\/|\?|#|$)/i.test(u)) { return false; }
    return true;
}

export function blockedError(url?: string): any {
    const config = { url: url ? String(url) : '' };
    const e: any = new Error('');
    e.__spBlocked = true;
    e.config = config;
    e.response = { status: 0, statusText: '', data: '', headers: {}, config: config };
    return e;
}

type ProbeResult = 'OK' | 'UNAUTHORIZED' | 'UNREACHABLE';

/* Проба идёт глобальным axios: инстанс дал бы цикл импортов, флаг __spProbe отсекает рекурсию */
function probeSessionDetailed(force?: boolean): Promise<ProbeResult> {
    if (probeInFlight) { return probeInFlight; }
    const now = Date.now();
    if (!force && now - lastProbeAt < PROBE_THROTTLE_MS) {
        return Promise.resolve('OK' as ProbeResult);
    }
    lastProbeAt = now;

    const token = readAuthToken();

    const cfg: any = { __spProbe: true, timeout: PROBE_TIMEOUT_MS, headers: {} };
    if (token) { cfg.headers.Authorization = 'Bearer ' + token; }

    probeInFlight = axios.get(joinUrl(apiBaseUrl(), '/api/config'), cfg).then(
        function (): ProbeResult {
            probeInFlight = null;
            return 'OK';
        },
        function (e: any): ProbeResult {
            probeInFlight = null;
            const response = e && e.response;
            if (!response) {
                return 'UNREACHABLE';
            }
            if (response.status === 401) {
                return 'UNAUTHORIZED';
            }
            if (response.status >= 500) {
                return 'UNREACHABLE';
            }
            return 'OK';
        },
    );
    return probeInFlight;
}

export function probeSession(force?: boolean): Promise<boolean> {
    return probeSessionDetailed(force).then(function (r: ProbeResult): boolean {
        return r !== 'UNAUTHORIZED';
    });
}

export function isProbe(error: any): boolean {
    return !!(error && ((error.config && error.config.__spProbe) || error.__spProbe));
}

const BUSY_RESET_MS = 5000;

function beginNavigation(action: string): void {
    gate.busy = action;
    window.setTimeout(function (): void { gate.busy = ''; }, BUSY_RESET_MS);
}

/* Сначала чистка ключей, потом навигация с перезагрузкой: иначе Auth.created() отбросит на '/' */
function goToLogin(): void {
    if (gate.busy) { return; }
    beginNavigation('reloginButton');
    if (!dropOwnSession()) { return; }
    noteLeavingToLogin();
    window.location.replace('/login');
}

function reloadPage(): void {
    if (gate.busy) { return; }
    beginNavigation('reloadButton');
    window.location.reload();
}

function retryNow(): void {
    if (gate.busy) { return; }
    gate.busy = 'retryButton';
    const probed = readAuthToken();
    probeSessionDetailed(true).then(function (result: ProbeResult): void {
        gate.busy = '';
        if (result === 'OK') {
            noteSuccess();
            return;
        }
        if (result === 'UNAUTHORIZED') {
            raise({ kind: 'SESSION_EXPIRED', sub: 'RETRY_CONFIRMED', url: '', status: 401 }, probed);
            return;
        }
        pulse('intent');
    });
}

const NETWORK_WINDOW_MS = 10000;
const NETWORK_THRESHOLD = 3;

let networkFailures: { [kind: string]: number[] } = {};

function serverAnswered(error: any, v: Verdict, env: FailureEnv): boolean {
    const response = error ? error.response : null;
    if (!response || !response.status) { return false; }
    if (!isOurBackend(v.url, env)) { return false; }
    return v.kind !== 'BACKEND_UNREACHABLE'
        && v.kind !== 'MIXED_CONTENT_BLOCKED'
        && v.kind !== 'OFFLINE';
}

export function report(error: any): void {
    if (isProbe(error)) { return; }

    const env = snapshotEnv(axios.isCancel ? axios.isCancel(error) : false);
    const v: Verdict = classify(error, env);

    if (v.kind === 'IGNORED') {
        if (v.sub === 'BLOCKED_BY_GATE') { pulse('request'); }
        return;
    }

    if (serverAnswered(error, v, env)) { noteSuccess(); }

    if (v.kind === 'OFFLINE') {
        requestFailedSinceIntent = true;
        raise(v);
        return;
    }

    if (v.kind === 'BACKEND_UNREACHABLE' || v.kind === 'MIXED_CONTENT_BLOCKED') {
        if (!isOurHost(v.url, env)) { return; }
        requestFailedSinceIntent = true;
        noteNetworkFailure(v);
        pulse('request');
        return;
    }

    if (v.kind === 'SESSION_EXPIRED' || v.kind === 'AUTH_STORAGE_CORRUPT') {
        raise(v, bearerOf(error));
        pulse('request');
        return;
    }
}

export function noteSuccess(): void {
    backendAnswered = true;
    networkFailures = {};
    clearTransient();
}

function noteNetworkFailure(v: Verdict): void {
    const now = Date.now();
    const seen = networkFailures[v.kind] ? networkFailures[v.kind] : [];
    const fresh = seen.filter(function (t: number): boolean {
        return now - t <= NETWORK_WINDOW_MS;
    });
    fresh.push(now);
    networkFailures[v.kind] = fresh;
    if (fresh.length >= NETWORK_THRESHOLD) {
        raise(v);
    }
}

let gateVm: Vue | null = null;
let guardInstalled = false;

function mountGate(): void {
    if (gateVm) { return; }
    if (!document.body) {
        document.addEventListener('DOMContentLoaded', mountGate);
        return;
    }

    gateVm = new Vue({
        i18n: i18n,
        render: function (h: any): any {
            return h(SessionGate, {
                props: { state: gate },
                on: { relogin: goToLogin, reload: reloadPage, retry: retryNow },
            });
        },
    });
    gateVm.$mount();

    const el = gateVm.$el;
    if (el && el.setAttribute) { el.setAttribute('data-sp-session-gate-host', '1'); }
    document.body.appendChild(el);
}

function onWake(): void {
    if (document.hidden) { return; }
    // Снимаем флаг выгрузки: после отменённого пользователем beforeunload pageshow не придёт
    clearUnloading();
    if (isFatal()) {
        if (needsRelogin() && foreignFreshToken()) { resumeWhenReady(); }
        return;
    }
    if (isLoginPage() || isAuthCallbackPage()) { return; }

    const kind = readLocalTokenKind();
    if (kind === 'CORRUPT' || kind === 'MALFORMED') {
        raise({ kind: 'AUTH_STORAGE_CORRUPT', sub: 'ON_WAKE', url: '', status: null });
        return;
    }
    if (kind === 'ABSENT') {
        if (!isLocalStorageUsable()) { return; }
        raise({ kind: 'SESSION_EXPIRED', sub: 'NO_TOKEN', url: '', status: null });
        return;
    }
    if (kind !== 'EXPIRED') { return; }

    // Токен снимаем до пробы: пока она в полёте, в соседней вкладке могли войти заново
    const probed = readAuthToken();
    probeSession().then(function (alive: boolean): void {
        if (!alive) {
            raise({ kind: 'SESSION_EXPIRED', sub: 'LOCAL_EXP_CONFIRMED', url: '', status: 401 }, probed);
        }
    });
}

function onStorage(e: StorageEvent): void {
    if (isLoginPage() || isAuthCallbackPage()) { return; }

    if (e.key === 'userData') {
        if (e.newValue && resumeTimer) { resumeWithNewSession(); }
        return;
    }

    if (e.key !== 'AUTH') { return; }

    if (e.newValue) {
        if (needsRelogin()) { resumeWhenReady(); }
        return;
    }

    cancelResume();

    if (logoutMarked()) {
        raise({ kind: 'SESSION_EXPIRED', sub: 'LOGGED_OUT_ELSEWHERE', url: '', status: null });
    }
}

function onOnline(): void {
    if (gate.kind === 'OFFLINE') { clearTransient(); }
    if (!isFatal()) { onWake(); }
}

function onOffline(): void {
    if (isFatal()) { return; }
    raise({ kind: 'OFFLINE', sub: 'EVENT', url: '', status: null });
}

const INTERACTIVE_TAGS: { [tag: string]: boolean } = {
    A: true, BUTTON: true, INPUT: true, SELECT: true, TEXTAREA: true, LABEL: true,
};

/* Половина элементов antd не имеет нативного тега, но роль ARIA проставляет всегда */
const INTERACTIVE_ROLES: { [role: string]: boolean } = {
    button: true, checkbox: true, combobox: true, gridcell: true, link: true,
    menuitem: true, menuitemcheckbox: true, menuitemradio: true, option: true,
    radio: true, searchbox: true, slider: true, spinbutton: true, 'switch': true,
    tab: true, textbox: true, treeitem: true,
};

function insideGate(el: Element | null): boolean {
    let node: Element | null = el;
    while (node) {
        if (node.classList && node.classList.contains('sp-gate-root')) { return true; }
        node = node.parentElement;
    }
    return false;
}

function isInteractive(el: Element | null): boolean {
    let node: Element | null = el;
    let depth = 0;
    while (node && depth < 8) {
        if (INTERACTIVE_TAGS[node.tagName] === true) { return true; }
        const role = node.getAttribute ? node.getAttribute('role') : null;
        if (role && INTERACTIVE_ROLES[role] === true) { return true; }
        if (node.classList && (node.classList.contains('ant-btn')
            || node.classList.contains('ant-menu-item')
            || node.classList.contains('ant-table-row'))) { return true; }
        node = node.parentElement;
        depth += 1;
    }
    return false;
}

function onUserAction(ev: Event): void {
    const target = ev.target as Element | null;
    if (insideGate(target)) { return; }
    if (!isInteractive(target)) { return; }
    noteIntent();
    if (!isFatal()) { return; }
    pulse('intent');
}

const EDITABLE_TAGS: { [tag: string]: boolean } = {
    INPUT: true, TEXTAREA: true, SELECT: true,
};

function isEditableTarget(el: Element | null): boolean {
    let node: Element | null = el;
    let depth = 0;
    while (node && depth < 8) {
        if (EDITABLE_TAGS[node.tagName]) { return true; }
        if ((node as any).isContentEditable === true) { return true; }
        if (node.getAttribute && node.getAttribute('contenteditable') === 'true') { return true; }
        node = node.parentElement;
        depth += 1;
    }
    return false;
}

function onKeyDown(ev: KeyboardEvent): void {
    const k = ev.key;
    if (k !== 'Enter' && k !== ' ' && k !== 'Spacebar') { return; }
    const target = ev.target as Element | null;
    if (insideGate(target)) { return; }

    if (isEditableTarget(target)) {
        if (k === 'Enter') { noteIntent(); }
        return;
    }
    if (!isInteractive(target)) { return; }

    noteIntent();
    if (!isFatal()) { return; }
    pulse('intent');
}

export function installSessionGuard(): void {
    if (isKeycloakMode()) { return; }
    if (guardInstalled) { return; }
    guardInstalled = true;

    installQuietWrapper();

    // Именно addEventListener: свойство window.onbeforeunload занято под closeSocket в App.vue
    window.addEventListener('beforeunload', markUnloading);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onWake);
    document.addEventListener('visibilitychange', onWake);
    document.addEventListener('pointerdown', onUserAction, true);
    document.addEventListener('click', onUserAction, true);
    document.addEventListener('keydown', onKeyDown, true);

    // Инстанс из axio-plugin.ts интерцепторы глобального axios не наследует — вешаем и туда
    axios.interceptors.response.use(
        function (res: any): any { return res; },
        function (error: any): any {
            report(error);
            return Promise.reject(error);
        },
    );

    const probeUrl = joinUrl(apiBaseUrl(), '/api/config');
    if (isMixedContentSuspect(window.location.protocol, probeUrl)) {
        console.error(
            '[session-guard] MIXED CONTENT: страница по https, а VUE_APP_BACKEND_URL='
            + apiBaseUrl()
            + ' — браузер, скорее всего, заблокирует все XHR. Требуется пересборка стенда.',
        );
    }
}
